import React, { useMemo, useRef, useState } from 'react';
import { AlertCircle, FileUp, Plus } from 'lucide-react';
import type { Quote, QuoteStatus, SupplyChainPlan } from '../../../types/supplyChain';
import type { SiteDocument } from '../../../types/siteRag';
import { cn } from '../../../lib/utils';

export interface ManualQuoteDraft {
  bomItemId: string;
  documentId?: string;
  supplierId: string;
  unitCostUsd: number;
  extendedPriceUsd?: number;
  leadTimeMonths: number;
  receivedAt: string;
  expiresAt: string;
  validityDays?: number;
  depositAmountUsd?: number;
  paymentTerms?: string;
  freightTerms?: string;
  deliveryWindowStart?: string;
  deliveryWindowEnd?: string;
  certificationComplianceConfirmed?: boolean;
  certificationComplianceNotes?: string;
  productionSlotStatus?: 'unknown' | 'available' | 'limited' | 'unavailable' | 'reserved';
  productionSlotNotes?: string;
  capacityAvailabilityStatus?: 'unknown' | 'available' | 'limited' | 'unavailable' | 'reserved';
  capacityAvailabilityNotes?: string;
  notes?: string;
}

export interface SupplyChainQuotesPanelProps {
  plan: SupplyChainPlan | undefined;
  documents?: readonly Pick<SiteDocument, 'id' | 'fileName'>[];
  onDecision?: (quote: Quote, action: 'award' | 'reject') => void;
  onUploadQuoteFile?: (file: File) => void | Promise<void>;
  onAddManualQuote?: (draft: ManualQuoteDraft) => void | Promise<void>;
  onAttachQuoteDocument?: (quote: Quote) => void | Promise<void>;
}

const STATUS_LABEL: Record<QuoteStatus, string> = {
  open: 'Open',
  awarded: 'Awarded',
  rejected: 'Rejected',
  expired: 'Expired',
};

function statusTone(status: QuoteStatus): string {
  switch (status) {
    case 'open':
      return 'bg-primary/15 text-primary';
    case 'awarded':
      return 'bg-success/15 text-success';
    case 'rejected':
      return 'bg-error/15 text-error';
    case 'expired':
      return 'bg-muted text-text-secondary';
  }
}

function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
  return `$${value.toFixed(0)}`;
}

/**
 * Render a quote date at day precision, in the VIEWER'S zone.
 *
 * Deliberately not pinned to UTC. `receivedAt` is a MIXED field with two
 * live producers: the manual form below (a `<input type="date">` day) and
 * the RFQ ingest path, where it is `document.uploadedAt` — a genuine
 * wall-clock instant (services/knowledge/documentUpload.ts:1602). Forcing
 * UTC here would fix the first and BREAK the second: someone uploading at
 * 19:30 in Chicago would see tomorrow's date.
 *
 * The manual path is made safe at the WRITE side instead — it now stores
 * local midnight rather than UTC midnight — so both producers are true
 * instants and local rendering is correct for both. `expiresAt` is derived
 * from `receivedAt` and inherits the same treatment.
 */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/**
 * Turn a `<input type="date">` value ('YYYY-MM-DD') into an ISO instant at LOCAL
 * midnight of that day.
 *
 * `new Date('2026-09-15')` is specified to parse a date-only form as UTC, so the
 * naive round-trip stores 00:00Z and renders as 14 September for anyone west of
 * Greenwich. Building from the parts puts the instant at local midnight, so the
 * day the user picked is the day they read back — and the value stays a genuine
 * instant, consistent with the RFQ ingest path that writes the same field.
 */
function isoFromDateInput(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return new Date(value).toISOString();  // unchanged fallback
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day)).toISOString();
}

export function isNeedsReview(q: Quote): boolean {
  return q.status === 'open';
}

type StatusFilter = 'all' | QuoteStatus | 'needs-review';

export const SupplyChainQuotesPanel: React.FC<SupplyChainQuotesPanelProps> = ({
  plan,
  documents = [],
  onDecision,
  onUploadQuoteFile,
  onAddManualQuote,
  onAttachQuoteDocument,
}) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualForm, setManualForm] = useState({
    bomItemId: '',
    documentId: '',
    supplierId: '',
    unitCostUsd: '',
    leadTimeMonths: '',
    receivedAt: '',
    expiresAt: '',
    validityDays: '',
    depositAmountUsd: '',
    paymentTerms: '',
    freightTerms: '',
    deliveryWindowStart: '',
    deliveryWindowEnd: '',
    certificationComplianceConfirmed: 'unknown',
    certificationComplianceNotes: '',
    productionSlotStatus: 'unknown',
    productionSlotNotes: '',
    capacityAvailabilityStatus: 'unknown',
    capacityAvailabilityNotes: '',
    notes: '',
  });
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const needsReviewCount = useMemo(() => {
    if (!plan) return 0;
    return plan.quotes.filter(isNeedsReview).length;
  }, [plan]);

  const rows = useMemo(() => {
    if (!plan) return [] as { quote: Quote; bomItemName: string; supplierName: string }[];
    const bomById = new Map(plan.bom.map((i) => [i.id, i]));
    const supplierById = new Map(plan.suppliers.map((s) => [s.id, s]));
    return plan.quotes
      .filter((q) => {
        if (statusFilter === 'all') return true;
        if (statusFilter === 'needs-review') return isNeedsReview(q);
        return q.status === statusFilter;
      })
      .map((q) => ({
        quote: q,
        bomItemName: bomById.get(q.bomItemId)?.name ?? '(unknown BoM item)',
        supplierName: supplierById.get(q.supplierId)?.name ?? '(unknown supplier)',
      }));
  }, [plan, statusFilter]);

  const openManual = () => {
    setManualError(null);
    setManualForm({
      bomItemId: plan?.bom[0]?.id ?? '',
      documentId: '',
      supplierId: plan?.suppliers[0]?.id ?? '',
      unitCostUsd: '',
      leadTimeMonths: '',
      receivedAt: '',
      expiresAt: '',
      validityDays: '',
      depositAmountUsd: '',
      paymentTerms: '',
      freightTerms: '',
      deliveryWindowStart: '',
      deliveryWindowEnd: '',
      certificationComplianceConfirmed: 'unknown',
      certificationComplianceNotes: '',
      productionSlotStatus: 'unknown',
      productionSlotNotes: '',
      capacityAvailabilityStatus: 'unknown',
      capacityAvailabilityNotes: '',
      notes: '',
    });
    setManualOpen(true);
  };

  const submitManual = async (e: React.FormEvent) => {
    e.preventDefault();
    const unitCostUsd = Number(manualForm.unitCostUsd);
    const leadTimeMonths = Number(manualForm.leadTimeMonths);
    const validityDays = manualForm.validityDays.trim() === '' ? null : Number(manualForm.validityDays);
    const depositAmountUsd = manualForm.depositAmountUsd.trim() === '' ? null : Number(manualForm.depositAmountUsd);
    const bomItem = plan?.bom.find(b => b.id === manualForm.bomItemId);
    if (!manualForm.bomItemId || !manualForm.supplierId || !manualForm.receivedAt || !manualForm.expiresAt) {
      setManualError('BoM item, supplier, received date, and expiry date are required.');
      return;
    }
    if (!Number.isFinite(unitCostUsd) || unitCostUsd <= 0) {
      setManualError('Unit cost must be greater than zero.');
      return;
    }
    if (!Number.isFinite(leadTimeMonths) || leadTimeMonths < 0) {
      setManualError('Lead time must be zero or greater.');
      return;
    }
    if (validityDays != null && (!Number.isFinite(validityDays) || validityDays < 0)) {
      setManualError('Quote validity must be zero or greater.');
      return;
    }
    if (depositAmountUsd != null && (!Number.isFinite(depositAmountUsd) || depositAmountUsd < 0)) {
      setManualError('Deposit amount must be zero or greater.');
      return;
    }
    if (manualForm.documentId && documents.filter(doc => doc.id === manualForm.documentId).length !== 1) {
      setManualError('This source document is no longer attached to the site. Choose another document or None.');
      return;
    }
    try {
      await onAddManualQuote?.({
        bomItemId: manualForm.bomItemId,
        ...(manualForm.documentId ? { documentId: manualForm.documentId } : {}),
        supplierId: manualForm.supplierId,
        unitCostUsd,
        ...(bomItem ? { extendedPriceUsd: bomItem.quantity * unitCostUsd } : {}),
        leadTimeMonths,
        // `new Date('2026-09-15')` parses as UTC midnight, which then renders as the
        // PREVIOUS day for every viewer west of Greenwich. These are instants shared
        // with the RFQ ingest path, so the field cannot simply be pinned to UTC at
        // render time; instead a picked day is stored as LOCAL midnight, which reads
        // back as the day that was picked.
        receivedAt: isoFromDateInput(manualForm.receivedAt),
        expiresAt: isoFromDateInput(manualForm.expiresAt),
        ...(validityDays != null ? { validityDays } : {}),
        ...(depositAmountUsd != null ? { depositAmountUsd } : {}),
        ...(manualForm.paymentTerms.trim() ? { paymentTerms: manualForm.paymentTerms.trim() } : {}),
        ...(manualForm.freightTerms.trim() ? { freightTerms: manualForm.freightTerms.trim() } : {}),
        ...(manualForm.deliveryWindowStart ? { deliveryWindowStart: new Date(manualForm.deliveryWindowStart).toISOString() } : {}),
        ...(manualForm.deliveryWindowEnd ? { deliveryWindowEnd: new Date(manualForm.deliveryWindowEnd).toISOString() } : {}),
        ...(manualForm.certificationComplianceConfirmed === 'yes' ? { certificationComplianceConfirmed: true } : {}),
        ...(manualForm.certificationComplianceConfirmed === 'no' ? { certificationComplianceConfirmed: false } : {}),
        ...(manualForm.certificationComplianceNotes.trim()
          ? { certificationComplianceNotes: manualForm.certificationComplianceNotes.trim() }
          : {}),
        ...(manualForm.productionSlotStatus ? { productionSlotStatus: manualForm.productionSlotStatus as ManualQuoteDraft['productionSlotStatus'] } : {}),
        ...(manualForm.productionSlotNotes.trim() ? { productionSlotNotes: manualForm.productionSlotNotes.trim() } : {}),
        ...(manualForm.capacityAvailabilityStatus
          ? { capacityAvailabilityStatus: manualForm.capacityAvailabilityStatus as ManualQuoteDraft['capacityAvailabilityStatus'] }
          : {}),
        ...(manualForm.capacityAvailabilityNotes.trim()
          ? { capacityAvailabilityNotes: manualForm.capacityAvailabilityNotes.trim() }
          : {}),
        ...(manualForm.notes.trim() ? { notes: manualForm.notes.trim() } : {}),
      });
      setManualOpen(false);
    } catch (error) {
      setManualError(error instanceof Error ? error.message : 'Quote could not be saved. Try again.');
    }
  };

  const sourceDocumentField = (
    <label className="block space-y-1 type-body text-text-secondary">
      <span>Source document</span>
      <select
        value={manualForm.documentId}
        onChange={(e) => setManualForm(prev => ({ ...prev, documentId: e.target.value }))}
        className="w-full px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
        data-testid="scm-quotes-manual-source"
      >
        <option value="">None</option>
        {documents.filter(doc => doc.id && documents.filter(candidate => candidate.id === doc.id).length === 1)
          .map(doc => <option key={doc.id} value={doc.id}>{doc.fileName}</option>)}
      </select>
    </label>
  );

  if (!plan || plan.quotes.length === 0) {
    return (
      <>
        <div className="text-center py-8 space-y-3 border border-dashed border-border" data-testid="scm-quotes-empty">
          <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest">
            Quotes
          </div>
          <p className="type-body text-text-secondary max-w-md mx-auto">
            No vendor quotes recorded yet. Upload a supplier quote PDF directly or capture it manually.
          </p>
          <div className="flex items-center justify-center gap-2">
            <input
              ref={uploadInputRef}
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file || !onUploadQuoteFile) return;
                void onUploadQuoteFile(file);
                e.currentTarget.value = '';
              }}
              data-testid="scm-quotes-upload-input"
            />
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              disabled={!onUploadQuoteFile}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 border type-body font-bold uppercase tracking-widest',
                onUploadQuoteFile
                  ? 'border-primary/40 text-primary hover:bg-primary/10'
                  : 'border-border text-text-secondary cursor-not-allowed',
              )}
              data-testid="scm-quotes-upload-cta"
            >
              <FileUp className="w-3 h-3" />
              Upload quote PDF
            </button>
            <button
              type="button"
              onClick={openManual}
              disabled={!onAddManualQuote}
              className={cn(
                'inline-flex items-center gap-2 px-3 py-1.5 border type-body font-bold uppercase tracking-widest',
                onAddManualQuote
                  ? 'border-primary/40 text-primary hover:bg-primary/10'
                  : 'border-border text-text-secondary cursor-not-allowed',
              )}
              data-testid="scm-quotes-manual-open"
            >
              <Plus className="w-3 h-3" />
              Add manually
            </button>
          </div>
        </div>
        {manualOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-label="Manual quote entry">
            <form
              onSubmit={(e) => { void submitManual(e); }}
              className="w-full max-w-xl bg-background border border-border p-4 space-y-3"
              data-testid="scm-quotes-manual-dialog"
            >
              <div className="type-section text-foreground">Add quote manually</div>
              {sourceDocumentField}
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={manualForm.bomItemId}
                  onChange={(e) => setManualForm(prev => ({ ...prev, bomItemId: e.target.value }))}
                  className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                  data-testid="scm-quotes-manual-bom"
                >
                  <option value="">Select BoM item *</option>
                  {plan?.bom.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <select
                  value={manualForm.supplierId}
                  onChange={(e) => setManualForm(prev => ({ ...prev, supplierId: e.target.value }))}
                  className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                  data-testid="scm-quotes-manual-supplier"
                >
                  <option value="">Select supplier *</option>
                  {plan?.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <input type="number" min={0} step="any" placeholder="Unit price USD *" value={manualForm.unitCostUsd} onChange={(e) => setManualForm(prev => ({ ...prev, unitCostUsd: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-price" />
                <input type="number" min={0} step="1" placeholder="Lead months *" value={manualForm.leadTimeMonths} onChange={(e) => setManualForm(prev => ({ ...prev, leadTimeMonths: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-lead" />
                <input type="date" value={manualForm.receivedAt} onChange={(e) => setManualForm(prev => ({ ...prev, receivedAt: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-received" />
                <input type="date" value={manualForm.expiresAt} onChange={(e) => setManualForm(prev => ({ ...prev, expiresAt: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-expires" />
                <input type="number" min={0} step="1" placeholder="Quote validity (days)" value={manualForm.validityDays} onChange={(e) => setManualForm(prev => ({ ...prev, validityDays: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-validity-days" />
                <input type="number" min={0} step="any" placeholder="Deposit amount USD" value={manualForm.depositAmountUsd} onChange={(e) => setManualForm(prev => ({ ...prev, depositAmountUsd: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-deposit" />
                <input value={manualForm.paymentTerms} onChange={(e) => setManualForm(prev => ({ ...prev, paymentTerms: e.target.value }))} className="col-span-2 px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" placeholder="Payment terms" data-testid="scm-quotes-manual-payment" />
                <input value={manualForm.freightTerms} onChange={(e) => setManualForm(prev => ({ ...prev, freightTerms: e.target.value }))} className="col-span-2 px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" placeholder="Freight / incoterms" data-testid="scm-quotes-manual-freight" />
                <input type="date" value={manualForm.deliveryWindowStart} onChange={(e) => setManualForm(prev => ({ ...prev, deliveryWindowStart: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-delivery-start" />
                <input type="date" value={manualForm.deliveryWindowEnd} onChange={(e) => setManualForm(prev => ({ ...prev, deliveryWindowEnd: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-delivery-end" />
                <select value={manualForm.certificationComplianceConfirmed} onChange={(e) => setManualForm(prev => ({ ...prev, certificationComplianceConfirmed: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-cert-confirmed">
                  <option value="unknown">Certification confirmation: unknown</option>
                  <option value="yes">Certification confirmed</option>
                  <option value="no">Certification not confirmed</option>
                </select>
                <select value={manualForm.productionSlotStatus} onChange={(e) => setManualForm(prev => ({ ...prev, productionSlotStatus: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-slot-status">
                  <option value="unknown">Production slot: unknown</option>
                  <option value="available">Production slot: available</option>
                  <option value="limited">Production slot: limited</option>
                  <option value="unavailable">Production slot: unavailable</option>
                  <option value="reserved">Production slot: reserved</option>
                </select>
                <select value={manualForm.capacityAvailabilityStatus} onChange={(e) => setManualForm(prev => ({ ...prev, capacityAvailabilityStatus: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" data-testid="scm-quotes-manual-capacity-status">
                  <option value="unknown">Capacity availability: unknown</option>
                  <option value="available">Capacity availability: available</option>
                  <option value="limited">Capacity availability: limited</option>
                  <option value="unavailable">Capacity availability: unavailable</option>
                  <option value="reserved">Capacity availability: reserved</option>
                </select>
                <input value={manualForm.certificationComplianceNotes} onChange={(e) => setManualForm(prev => ({ ...prev, certificationComplianceNotes: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" placeholder="Certification notes" data-testid="scm-quotes-manual-cert-notes" />
                <input value={manualForm.productionSlotNotes} onChange={(e) => setManualForm(prev => ({ ...prev, productionSlotNotes: e.target.value }))} className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" placeholder="Production slot notes" data-testid="scm-quotes-manual-slot-notes" />
                <input value={manualForm.capacityAvailabilityNotes} onChange={(e) => setManualForm(prev => ({ ...prev, capacityAvailabilityNotes: e.target.value }))} className="col-span-2 px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" placeholder="Capacity availability notes" data-testid="scm-quotes-manual-capacity-notes" />
                <textarea value={manualForm.notes} onChange={(e) => setManualForm(prev => ({ ...prev, notes: e.target.value }))} className="col-span-2 min-h-[4rem] px-2 py-1.5 bg-background border border-border text-[10px] text-foreground" placeholder="Notes" data-testid="scm-quotes-manual-notes" />
              </div>
              {manualError && (
                <div className="type-body text-danger" data-testid="scm-quotes-manual-error">{manualError}</div>
              )}
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={() => setManualOpen(false)} className="px-2 py-1 type-body uppercase tracking-widest border border-border text-text-secondary">Cancel</button>
                <button type="submit" className="px-2 py-1 type-body uppercase tracking-widest rounded-md border border-primary/40 text-primary hover:bg-primary/10" data-testid="scm-quotes-manual-submit">Save quote</button>
              </div>
            </form>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-3" data-testid="scm-quotes-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[10px] font-bold text-text-muted uppercase tracking-widest">
          <span>Quotes — {rows.length} of {plan.quotes.length}</span>
          {needsReviewCount > 0 && (
            <button
              type="button"
              onClick={() => setStatusFilter('needs-review')}
              className={cn(
                'inline-flex items-center gap-1 px-1.5 py-0.5 border border-warning/40 text-warning type-body tracking-widest',
                statusFilter === 'needs-review'
                  ? 'bg-warning/20'
                  : 'bg-warning/10 hover:bg-warning/20',
              )}
              data-testid="scm-quotes-needs-review-badge"
              title="Filter to incoming quotes that need triage."
            >
              <AlertCircle className="w-3 h-3" />
              {needsReviewCount} need{needsReviewCount === 1 ? 's' : ''} review
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input
            ref={uploadInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file || !onUploadQuoteFile) return;
              void onUploadQuoteFile(file);
              e.currentTarget.value = '';
            }}
            data-testid="scm-quotes-upload-input"
          />
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            disabled={!onUploadQuoteFile}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-1 type-body uppercase tracking-widest border',
              onUploadQuoteFile
                ? 'border-primary/40 text-primary hover:bg-primary/10'
                : 'border-border text-text-secondary cursor-not-allowed',
            )}
            data-testid="scm-quotes-upload-cta"
          >
            <FileUp className="w-3 h-3" />
            Upload quote
          </button>
          <button
            type="button"
            onClick={openManual}
            disabled={!onAddManualQuote}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-1 type-body uppercase tracking-widest border',
              onAddManualQuote
                ? 'border-primary/40 text-primary hover:bg-primary/10'
                : 'border-border text-text-secondary cursor-not-allowed',
            )}
            data-testid="scm-quotes-manual-open"
          >
            <Plus className="w-3 h-3" />
            Add manually
          </button>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
            data-testid="scm-quotes-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="needs-review">Needs review ({needsReviewCount})</option>
            {(Object.keys(STATUS_LABEL) as QuoteStatus[]).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-[10px]" data-testid="scm-quotes-table">
          <thead className="bg-muted text-text-secondary">
            <tr>
              <th className="px-3 py-2 text-left font-bold uppercase tracking-widest type-body">BoM item</th>
              <th className="px-3 py-2 text-left font-bold uppercase tracking-widest type-body">Supplier</th>
              <th className="px-3 py-2 text-right font-bold uppercase tracking-widest type-body">Unit price</th>
              <th className="px-3 py-2 text-right font-bold uppercase tracking-widest type-body">Lead (mo)</th>
              <th className="px-3 py-2 text-left font-bold uppercase tracking-widest type-body">Received</th>
              <th className="px-3 py-2 text-left font-bold uppercase tracking-widest type-body">Expires</th>
              <th className="px-3 py-2 text-left font-bold uppercase tracking-widest type-body">Status</th>
              <th className="px-3 py-2 text-left font-bold uppercase tracking-widest type-body">Commercial terms</th>
              <th className="px-3 py-2 text-right font-bold uppercase tracking-widest type-body">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ quote, bomItemName, supplierName }) => (
              <tr
                key={quote.id}
                className="border-t border-border hover:bg-muted/30"
                data-testid={`scm-quotes-row-${quote.id}`}
              >
                <td className="px-3 py-2 text-foreground">{bomItemName}</td>
                <td className="px-3 py-2 text-foreground">{supplierName}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatUsd(quote.unitCostUsd)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{quote.leadTimeMonths}</td>
                <td className="px-3 py-2 text-text-secondary">{formatDate(quote.receivedAt)}</td>
                <td className="px-3 py-2 text-text-secondary">{formatDate(quote.expiresAt)}</td>
                <td className="px-3 py-2">
                  <span
                    className={cn(
                      'px-1.5 py-0.5 type-meta font-bold uppercase tracking-widest',
                      statusTone(quote.status),
                    )}
                  >
                    {STATUS_LABEL[quote.status]}
                  </span>
                </td>
                <td className="px-3 py-2 text-text-secondary">
                  {[
                    quote.validityDays != null ? `Validity ${quote.validityDays}d` : null,
                    quote.depositAmountUsd != null ? `Deposit ${formatUsd(quote.depositAmountUsd)}` : null,
                    quote.paymentTerms ? quote.paymentTerms : null,
                    quote.freightTerms ? quote.freightTerms : null,
                    quote.attachments?.length ? `${quote.attachments.length} doc${quote.attachments.length === 1 ? '' : 's'}` : null,
                  ].filter(Boolean).join(' · ') || '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { void onAttachQuoteDocument?.(quote); }}
                      disabled={!onAttachQuoteDocument}
                      className={cn(
                        'type-body uppercase tracking-widest px-2 py-1 border',
                        onAttachQuoteDocument
                          ? 'border-border text-text-secondary hover:bg-muted/40'
                          : 'border-border text-text-secondary cursor-not-allowed',
                      )}
                      data-testid={`scm-quotes-attach-doc-${quote.id}`}
                    >
                      Attach doc
                    </button>
                    <button
                      type="button"
                      onClick={() => onDecision?.(quote, 'award')}
                      disabled={!onDecision || quote.status !== 'open'}
                      className={cn(
                        'type-body uppercase tracking-widest px-2 py-1 border',
                        onDecision && quote.status === 'open'
                          ? 'border-success/40 text-success hover:bg-success/10'
                          : 'border-border text-text-secondary cursor-not-allowed',
                      )}
                    >
                      Award
                    </button>
                    <button
                      type="button"
                      onClick={() => onDecision?.(quote, 'reject')}
                      disabled={!onDecision || quote.status !== 'open'}
                      className={cn(
                        'type-body uppercase tracking-widest px-2 py-1 border',
                        onDecision && quote.status === 'open'
                          ? 'border-danger/40 text-danger hover:bg-danger/10'
                          : 'border-border text-text-secondary cursor-not-allowed',
                      )}
                    >
                      Reject
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {manualOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-label="Manual quote entry">
          <form
            onSubmit={(e) => { void submitManual(e); }}
            className="w-full max-w-xl bg-background border border-border p-4 space-y-3"
            data-testid="scm-quotes-manual-dialog"
          >
            <div className="type-section text-foreground">Add quote manually</div>
            {sourceDocumentField}
            <div className="grid grid-cols-2 gap-2">
              <select
                value={manualForm.bomItemId}
                onChange={(e) => setManualForm(prev => ({ ...prev, bomItemId: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-bom"
              >
                <option value="">Select BoM item *</option>
                {plan.bom.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              <select
                value={manualForm.supplierId}
                onChange={(e) => setManualForm(prev => ({ ...prev, supplierId: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-supplier"
              >
                <option value="">Select supplier *</option>
                {plan.suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <input
                type="number"
                min={0}
                step="any"
                placeholder="Unit price USD *"
                value={manualForm.unitCostUsd}
                onChange={(e) => setManualForm(prev => ({ ...prev, unitCostUsd: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-price"
              />
              <input
                type="number"
                min={0}
                step="1"
                placeholder="Lead months *"
                value={manualForm.leadTimeMonths}
                onChange={(e) => setManualForm(prev => ({ ...prev, leadTimeMonths: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-lead"
              />
              <input
                type="date"
                value={manualForm.receivedAt}
                onChange={(e) => setManualForm(prev => ({ ...prev, receivedAt: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-received"
              />
              <input
                type="date"
                value={manualForm.expiresAt}
                onChange={(e) => setManualForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-expires"
              />
              <input
                type="number"
                min={0}
                step="1"
                placeholder="Quote validity (days)"
                value={manualForm.validityDays}
                onChange={(e) => setManualForm(prev => ({ ...prev, validityDays: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-validity-days"
              />
              <input
                type="number"
                min={0}
                step="any"
                placeholder="Deposit amount USD"
                value={manualForm.depositAmountUsd}
                onChange={(e) => setManualForm(prev => ({ ...prev, depositAmountUsd: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-deposit"
              />
              <input
                value={manualForm.paymentTerms}
                onChange={(e) => setManualForm(prev => ({ ...prev, paymentTerms: e.target.value }))}
                className="col-span-2 px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                placeholder="Payment terms"
                data-testid="scm-quotes-manual-payment"
              />
              <input
                value={manualForm.freightTerms}
                onChange={(e) => setManualForm(prev => ({ ...prev, freightTerms: e.target.value }))}
                className="col-span-2 px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                placeholder="Freight / incoterms"
                data-testid="scm-quotes-manual-freight"
              />
              <input
                type="date"
                value={manualForm.deliveryWindowStart}
                onChange={(e) => setManualForm(prev => ({ ...prev, deliveryWindowStart: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-delivery-start"
              />
              <input
                type="date"
                value={manualForm.deliveryWindowEnd}
                onChange={(e) => setManualForm(prev => ({ ...prev, deliveryWindowEnd: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-delivery-end"
              />
              <select
                value={manualForm.certificationComplianceConfirmed}
                onChange={(e) => setManualForm(prev => ({ ...prev, certificationComplianceConfirmed: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-cert-confirmed"
              >
                <option value="unknown">Certification confirmation: unknown</option>
                <option value="yes">Certification confirmed</option>
                <option value="no">Certification not confirmed</option>
              </select>
              <select
                value={manualForm.productionSlotStatus}
                onChange={(e) => setManualForm(prev => ({ ...prev, productionSlotStatus: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-slot-status"
              >
                <option value="unknown">Production slot: unknown</option>
                <option value="available">Production slot: available</option>
                <option value="limited">Production slot: limited</option>
                <option value="unavailable">Production slot: unavailable</option>
                <option value="reserved">Production slot: reserved</option>
              </select>
              <select
                value={manualForm.capacityAvailabilityStatus}
                onChange={(e) => setManualForm(prev => ({ ...prev, capacityAvailabilityStatus: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                data-testid="scm-quotes-manual-capacity-status"
              >
                <option value="unknown">Capacity availability: unknown</option>
                <option value="available">Capacity availability: available</option>
                <option value="limited">Capacity availability: limited</option>
                <option value="unavailable">Capacity availability: unavailable</option>
                <option value="reserved">Capacity availability: reserved</option>
              </select>
              <input
                value={manualForm.certificationComplianceNotes}
                onChange={(e) => setManualForm(prev => ({ ...prev, certificationComplianceNotes: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                placeholder="Certification notes"
                data-testid="scm-quotes-manual-cert-notes"
              />
              <input
                value={manualForm.productionSlotNotes}
                onChange={(e) => setManualForm(prev => ({ ...prev, productionSlotNotes: e.target.value }))}
                className="px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                placeholder="Production slot notes"
                data-testid="scm-quotes-manual-slot-notes"
              />
              <input
                value={manualForm.capacityAvailabilityNotes}
                onChange={(e) => setManualForm(prev => ({ ...prev, capacityAvailabilityNotes: e.target.value }))}
                className="col-span-2 px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                placeholder="Capacity availability notes"
                data-testid="scm-quotes-manual-capacity-notes"
              />
              <textarea
                value={manualForm.notes}
                onChange={(e) => setManualForm(prev => ({ ...prev, notes: e.target.value }))}
                className="col-span-2 min-h-[4rem] px-2 py-1.5 bg-background border border-border text-[10px] text-foreground"
                placeholder="Notes"
                data-testid="scm-quotes-manual-notes"
              />
            </div>
            {manualError && (
              <div className="type-body text-danger" data-testid="scm-quotes-manual-error">{manualError}</div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setManualOpen(false)}
                className="px-2 py-1 type-body uppercase tracking-widest border border-border text-text-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-2 py-1 type-body uppercase tracking-widest rounded-md border border-primary/40 text-primary hover:bg-primary/10"
                data-testid="scm-quotes-manual-submit"
              >
                Save quote
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
